import {Component, Inject, OnInit, ViewChild} from "@angular/core";
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef} from "@angular/material/legacy-dialog";
import {TranslateService} from "@ngx-translate/core";
import {GeographieView} from "@domain/geographie/geographieView";
import {TypeGeographie} from "@domain/geographie/typeGeographie";
import {AutocompleteComponent} from "@share/component/autocomplete/autocomplete.component";
import {PorteeGeographie} from "@share/component/autocomplete/options/geographie";

/**
 * Configuration d'un type de géographie
 */
export interface GeographieTypeConfig {
    type: TypeGeographie;
    libelle: string;
    icon: string;
    autocompleteType: string;
}

/**
 * Données d'entrée de la popin
 */
export interface GeographieSelectionPopinData {
    listeTypes: TypeGeographie[];
    contexte?: PorteeGeographie;
    title?: string;
}


/**
 * Composant popup de sélection géographique
 * */
@Component({
    selector: 'geographie-selection-popin',
    templateUrl: './geographie-selection-popin.component.html'
})
export class GeographieSelectionPopinComponent implements OnInit {
    //Configuration des types disponibles
    private static readonly TYPE_CONFIGS: Map<TypeGeographie, GeographieTypeConfig> = new Map([
        [TypeGeographie.ZONE, {type: TypeGeographie.ZONE, libelle: 'geographie.zone', icon: 'language', autocompleteType: 'ZONE'}],
        [TypeGeographie.PAYS, {type: TypeGeographie.PAYS, libelle: 'geographie.pays', icon: 'language', autocompleteType: 'PAYS'}],
        [TypeGeographie.REGION, {type: TypeGeographie.REGION, libelle: 'geographie.territoire', icon: 'language', autocompleteType: 'REGION'}],
        [TypeGeographie.REGION_ADMINISTRATIVE, {type: TypeGeographie.REGION_ADMINISTRATIVE, libelle: 'geographie.region', icon: 'language', autocompleteType: 'REGION_ADMINISTRATIVE'}],
        [TypeGeographie.DEPARTEMENT_ADMINISTRATIF, {type: TypeGeographie.DEPARTEMENT_ADMINISTRATIF, libelle: 'geographie.departement', icon: 'language', autocompleteType: 'DEPARTEMENT_ADMINISTRATIF'}],
        [TypeGeographie.VILLE, {type: TypeGeographie.VILLE, libelle: 'geographie.ville', icon: 'language', autocompleteType: 'VILLE'}]
    ]);

    /** Indique si on affiche les options de sélection de type */
    showOptions: boolean = true;

    /** Type de géographie sélectionné */
    selectedType: GeographieTypeConfig;

    /** Géographie sélectionnée */
    selectedGeographie: GeographieView;

    /** Liste des types de géographie disponibles */
    listeTypes: GeographieTypeConfig[] = [];

    /** Titre de la popin */
    title: string;

    /** Filtre pour l'autocomplete */
    autocompleteFilter: any;

    /** Référence vers le composant autocomplete */
    @ViewChild("autocompleteGeographie") autocompleteGeographie: AutocompleteComponent;

    /**
     * Constructeur
     * @param translateService Service de traduction
     * @param matDialogRef Référence vers la popup
     * @param data Données d'entrée de la popup
     */
    constructor(
        private translateService: TranslateService,
        private matDialogRef: MatDialogRef<GeographieSelectionPopinComponent>,
        @Inject(MAT_DIALOG_DATA) public data: GeographieSelectionPopinData
    ) {}

    /**
     * Initialisation du composant
     */
    ngOnInit(): void {
        this.title = this.data.title || this.translateService.instant('geographie.selection.title');
        this.listeTypes = (this.data.listeTypes || [])
            .map(type => GeographieSelectionPopinComponent.TYPE_CONFIGS.get(type))
            .filter(config => !!config);
    }

    /**
     * Sélectionne un type de géographie et affiche l'autocomplete
     * @param typeConfig Configuration du type sélectionné
     */
    selectType(typeConfig: GeographieTypeConfig): void {
        this.selectedType = typeConfig;
        this.showOptions = false;
        this.selectedGeographie = null;
        this.autocompleteFilter = {
            listeTypes: [typeConfig.autocompleteType],
            contexte: this.data.contexte || PorteeGeographie.INDEMNITE
        };
    }

    /**
     * Retourne à la sélection du type de géographie
     */
    goBack(): void {
        this.selectedType = null;
        this.showOptions = true;
        this.selectedGeographie = null;
        if (this.autocompleteGeographie) {
            this.autocompleteGeographie.listeItems = null;
            this.autocompleteGeographie.reset();
        }
    }

    /**
     * Gère le changement de géographie depuis l'autocomplete
     * @param geographie Géographie sélectionnée
     */
    onGeographieChange(geographie: GeographieView): void {
        this.selectedGeographie = geographie;
    }

    /**
     * Confirme la sélection et ferme la popin
     */
    confirm(): void {
        this.matDialogRef.close(this.selectedGeographie);
    }

    /**
     * Ferme la popin sans sélection
     */
    close(): void {
        this.matDialogRef.close();
    }
}